/**
 * Pure, sandboxed condition evaluator.
 *
 * Supports the operators defined in @app/studio-core:
 * atomic: ?, !, =, !=,<, >, <=, >=
 * compound: &, |
 *
 * Plus a few extensions used by real flows: in, match, and the
 * authoring-friendly aliases "and"/"or" for "&"/"|".
 *
 * Compound steps may list their sub-conditions under `conditions` or under
 * `v` (the shape the flow DSL uses: `op: or, v: [...]`). Numeric comparisons
 * also accept a nested `{ op: len, v:<string|array> }` operand so flows can
 * validate lengths without a server round-trip.
 */

import type { Condition } from "../types/flow";
import type { ExecutionContext } from "../context/execution-context";
import { resolveValue } from "./resolve";

function isPlainObject(value: unknown): value is Record<string, unknown> {
 return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function readConditionList(c: Record<string, unknown>): readonly unknown[] {
 if (Array.isArray(c["conditions"])) return c["conditions"] as readonly unknown[];
 if (Array.isArray(c["v"])) return c["v"] as readonly unknown[];
 return [];
}

/**
 * Resolve a comparison operand, unwrapping the nested `len` operator used by
 * flow authoring (e.g. `left: { op: len, v: "@form.message" }`). Without
 * this, the operand resolved to `{ op: "len", ... }` and every numeric
 * comparison became NaN → false, silently disabling length validation.
 */
function resolveOperand(value: unknown, ctx: ExecutionContext): unknown {
 const resolved = resolveValue(value, ctx);
 if (isPlainObject(resolved) && resolved["op"] === "len") {
 const target = resolveValue(resolved["v"], ctx);
 if (typeof target === "string" || Array.isArray(target)) return target.length;
 return NaN;
 }
 return resolved;
}

export function evaluateCondition(
 condition: Condition | unknown,
 ctx: ExecutionContext,
): boolean {
 if (!condition || typeof condition !== "object") {
 // Truthy literal
 return Boolean(resolveValue(condition, ctx));
 }
 const c = condition as Record<string, unknown>;
 const rawOp = c["op"];
 // "and"/"or" are accepted as authoring-friendly aliases of "&"/"|".
 const op = rawOp === "and" ? "&" : rawOp === "or" ? "|" : rawOp;

 switch (op) {
 case "?": {
 const v = resolveValue(c["v"], ctx);
 return v !== null && v !== undefined && v !== false && v !== "" && v !==0;
 }
 case "!": {
 const v = c["v"];
 if (v && typeof v === "object" && "op" in v) {
 return !evaluateCondition(v as Condition, ctx);
 }
 return !Boolean(resolveValue(v, ctx));
 }
 case "&": {
 return readConditionList(c).every((cond) => evaluateCondition(cond as Condition, ctx));
 }
 case "|": {
 return readConditionList(c).some((cond) => evaluateCondition(cond as Condition, ctx));
 }
 case "=":
 return looseEqual(
 resolveOperand(c["left"] ?? c["l"], ctx),
 resolveOperand(c["right"] ?? c["r"], ctx),
 );
 case "!=":
 return !looseEqual(
 resolveOperand(c["left"] ?? c["l"], ctx),
 resolveOperand(c["right"] ?? c["r"], ctx),
 );
 case "<":
 return numCompare(c, ctx, (a, b) => a< b);
 case ">":
 return numCompare(c, ctx, (a, b) => a > b);
 case "<=":
 return numCompare(c, ctx, (a, b) => a <= b);
 case ">=":
 return numCompare(c, ctx, (a, b) => a >= b);
 case "in": {
 const needle = resolveOperand(c["left"] ?? c["l"], ctx);
 const haystack = resolveOperand(c["right"] ?? c["r"], ctx);
 if (Array.isArray(haystack)) return haystack.includes(needle);
 if (typeof haystack === "string" && typeof needle === "string") {
 return haystack.includes(needle);
 }
 return false;
 }
 case "match": {
 const value = resolveOperand(c["left"] ?? c["l"], ctx);
 const pattern = resolveOperand(c["right"] ?? c["r"], ctx);
 if (typeof value !== "string" || typeof pattern !== "string") return false;
 try {
 return new RegExp(pattern).test(value);
 } catch {
 return false;
 }
 }
 default:
 return false;
 }
}

function looseEqual(a: unknown, b: unknown): boolean {
 if (a === b) return true;
 if (a === null || a === undefined) return b === null || b === undefined;
 if (typeof a === "number" && typeof b === "number") return a === b;
 return String(a) === String(b);
}

function numCompare(
 c: Record<string, unknown>,
 ctx: ExecutionContext,
 fn: (a: number, b: number) => boolean,
): boolean {
 const a = resolveOperand(c["left"] ?? c["l"], ctx);
 const b = resolveOperand(c["right"] ?? c["r"], ctx);
 const an = typeof a === "number" ? a : Number(a);
 const bn = typeof b === "number" ? b : Number(b);
 if (Number.isNaN(an) || Number.isNaN(bn)) return false;
 return fn(an, bn);
}